import React from 'react';
import type { CollapseProps } from 'antd';
import { Dayjs } from 'dayjs';
import { Collapse, ConfigProvider } from 'antd';
import { Form, useFormikContext } from 'formik';
import {
  AphDatePicker,
  AphSelect,
  useFetchServiceV3
} from '@aph/common-react';
import { defaultPageSize } from 'global.constants';
import { ILibrarySearchFormData } from 'types/form.types';
import { updateUrlSearchParameters } from 'global.helpers';
import { setupFilteredTagsList } from 'helpers/tag.helpers';
import {
  handleRemoveSelectedSearchFilterTag,
  setupDateForRequest
} from 'helpers/form.helpers';
import {
  ILectureSearchRequest,
  IUseSearchService,
  ILectureSearchResult,
  IGetLookupResponse
} from 'services';

import {
  NoResultsFound,
  SearchBar,
  SearchFilteredTags,
  SearchResultsWrapper,
  LectureLargeSearchResultItem
} from 'components';

import {
  LectureSearchResultItem
} from 'components';

import './LectureSearchInner.scss';

/**
 * CheckSearchRequestEmpty
 *
 * @description We need to check whether the user has search for anything or it is back to the initial request model
 * @note This method is used so we can render different UI when no search filters are applied
 * @param {ILectureSearchRequest | null | undefined} request Lecture search request model
 * @returns {boolean} True or false whether the search is filtered at all
 */
const checkSearchRequestEmpty = (request: ILectureSearchRequest | null | undefined): boolean => {
  if (!request) {
    return true;
  }

  // If the user is searching for a search term it is not an initial search
  if (request.searchTerms) {
    return false;
  }

  // If any filters are selected then we assume the user is not using a blank search
  if (request.topics && request.topics.length > 0) {
    return false;
  }

  if (request.dateFrom || request.dateTo) {
    return false;
  }

  return true;
};

/**
 * ILectureSearchInner
 *
 * @description Component properties for the publication search component
 */
interface ILectureSearchInner {
  searchService: IUseSearchService;
  currentSearchRequest: ILectureSearchRequest;
  searchResults: {
    loading: boolean;
    results: Array<ILectureSearchResult>;
    currentPage: number;
    pageSize: number;
    rowCount: number;
  };
  setSelectedVideo: React.Dispatch<string | null>;
  filterSearchResults: (request: ILectureSearchRequest) => Promise<void>;
  paginationHandler: (selectedPage: number) => Promise<void>;
}

/**
 * PublicationSearchInner
 *
 * @description
 */
const LectureSearchInner: React.FC<ILectureSearchInner> = ({
  searchService,
  searchResults,
  currentSearchRequest,
  filterSearchResults,
  paginationHandler,
  setSelectedVideo
}) => {
  const { items: topics } = useFetchServiceV3<undefined, IGetLookupResponse>(searchService.getSearchLectureTopics, { loadOnInitiate: true });

  const { values: formValues, setFieldValue } = useFormikContext<ILibrarySearchFormData>();

  /**
   * HandleFormFieldOnChange
   *
   * @description On change handler for filters
   * @param {string}                 key   Form field name
   * @param {string | Array<string>} value Field value
   * @returns {void}
   */
  const handleFormFieldOnChange = (key: string, value: string | Array<string>): void => {
    updateUrlSearchParameters(key, value);

    const updatedSearchRequest: ILectureSearchRequest = {
      ...currentSearchRequest,
      currentPage: 1,
      [key]: value
    };

    filterSearchResults(updatedSearchRequest);
  };

  /**
   * HandleDateFromFieldOnChange
   *
   * @description On change handler for the date field
   * @param {Dayjs | null} value Selected date values
   * @returns {void}
   */
  const handleDateFromFieldOnChange = (value: Dayjs | null | null): void => {
    const updatedUrlParameter = value ? value.format('YYYY-MM-DD') : null;
    updateUrlSearchParameters('dateFrom', updatedUrlParameter);
    const dataRequestSetup = setupDateForRequest(value);
    const updatedSearchRequest: ILectureSearchRequest = {
      ...currentSearchRequest,
      currentPage: 1,
      dateFrom: dataRequestSetup,
    };

    filterSearchResults(updatedSearchRequest);
  };

  /**
   * HandleDateToFieldOnChange
   *
   * @description On change handler for the date field
   * @param {Dayjs | null} value Selected date values
   * @returns {void}
   */
  const handleDateToFieldOnChange = (value: Dayjs | null | null): void => {
    const updatedUrlParameter = value ? value.format('YYYY-MM-DD') : null;

    updateUrlSearchParameters('dateTo', updatedUrlParameter);

    const dataRequestSetup = setupDateForRequest(value);
    const updatedSearchRequest: ILectureSearchRequest = {
      ...currentSearchRequest,
      currentPage: 1,
      dateTo: dataRequestSetup,
    };

    filterSearchResults(updatedSearchRequest);
  };

  // This defines the content for the collapse
  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'Filters',
      children:
        <div className='library-publication-search__filters-inner'>
          <div className='library-publication-search__filter-column'>
            <h3>Topics</h3>
            <AphSelect
              name='topics'
              mode='multiple'
              placeholder='Start typing to search...'
              options={topics ?? []}
              allowClear
              onChange={(value: string | Array<string>) => handleFormFieldOnChange('topics', value)}
            />
          </div>
          <div className='library-publication-search__filter-column'>
            <h3>When</h3>
            <div className='library-publication-search__filter-date'>
              <AphDatePicker
                name='dateFrom'
                placeholder='Start date'
                onChange={(value: Dayjs | null) => handleDateFromFieldOnChange(value)}
              />
              <AphDatePicker
                name='dateTo'
                placeholder='End date'
                onChange={(value: Dayjs | null) => handleDateToFieldOnChange(value)}
              />
            </div>
          </div>
        </div>
    }
  ];

  return (
    <div className='library-publication-search'>
      <Form>
        <div className='library-publication-search__filters'>
          <SearchBar theme='publications' name='searchTerms' showClear />
          <ConfigProvider
            theme={{
              token: {
                borderRadiusLG: 0,
                lineWidth: 0
              }
            }}
          >
            <Collapse
              className='library-publication-search__filters--additional'
              defaultActiveKey={['1']}
              items={items}
            />
          </ConfigProvider>
        </div>
        <SearchResultsWrapper
          loading={searchResults.loading && (!searchResults.results || searchResults.results.length <= 0)}
          paginationConfig={{
            currentPage: searchResults.currentPage ?? 1,
            totalResults: searchResults.rowCount ?? 0,
            pageSize: searchResults.pageSize ?? defaultPageSize,
            onPaginate: paginationHandler
          }}
        >
          <SearchFilteredTags
            tags={setupFilteredTagsList('topics', formValues.topics, topics)}
            onRemove={(id: string) =>
              handleRemoveSelectedSearchFilterTag('topics', id, formValues.topics, setFieldValue, handleFormFieldOnChange)}
          />
          {
            searchResults.results &&
            searchResults.results.length > 0
              ?
              <div className='library-lecture-search__results'>
                {
                  searchResults.results.map((searchResult, index) =>
                    // We only show the large video on the first page and if the user is using a default search
                    (index === 0 && checkSearchRequestEmpty(currentSearchRequest))
                    && searchResults.currentPage === 1
                      ?
                      <LectureLargeSearchResultItem
                        key={searchResult.id}
                        result={{...searchResult}}
                      />
                      :
                      <LectureSearchResultItem
                        key={searchResult.id}
                        setSelectedVideo={setSelectedVideo}
                        result={{
                          ...searchResult
                        }}
                      />
                  )
                }
              </div>
              : <NoResultsFound />
          }
        </SearchResultsWrapper>
      </Form>
    </div>
  );
};

export default LectureSearchInner;
